import { ShapeUtil, SVGContainer, Polyline2d, Circle2d, Rectangle2d, Group2d, type HandleDragInfo, Vec } from "./canvas";
import { draftingProps, type DraftingShape } from "../shared/schema";
import { wallGeometry } from "./wall-geometry";
import {
  draftingDefaults,
  draftingPath,
  curvePoints,
} from "../shared/drafting";

export function DraftingGlyph({ shape }: { shape: DraftingShape }) {
  const p = shape.props,
    a = p.points[0] || { x: 0, y: 0 },
    b = p.points.at(-1) || a;
  const common = {
    stroke: p.color,
    strokeWidth: p.weight,
    fill: "none",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (p.kind === "circle")
    return (
      <circle
        {...common}
        cx={a.x}
        cy={a.y}
        r={Math.hypot(b.x - a.x, b.y - a.y)}
      />
    );
  if (p.kind === "door" || p.kind === "window") {
    const w = p.width,
      t = p.thickness,
      side = p.flip ? -1 : 1;
    return (
      <g {...common}>
        <rect
          x={-w / 2}
          y={-t / 2 - 1}
          width={w}
          height={t + 2}
          fill="white"
          stroke="none"
        />
        <path d={`M${-w / 2},${-t / 2} v${t} M${w / 2},${-t / 2} v${t}`} />
        {p.kind === "window" ? (
          <>
            <rect x={-w / 2} y={-t / 2} width={w} height={t} />
            <path d={`M${-w / 2},0 h${w}`} />
          </>
        ) : (
          <>
            <path
              d={`M${-w / 2},0 v${side * w} M${-w / 2},${side * w} A${w},${w} 0 0 ${side > 0 ? 0 : 1} ${w / 2},0`}
            />
          </>
        )}
      </g>
    );
  }
  return (
    <g {...common}>
      {p.kind === "wall" ? (
        <>
          <path d={draftingPath(p)} strokeWidth={p.thickness + p.weight * 2} />
          <path
            d={draftingPath(p)}
            stroke="#f0efea"
            strokeWidth={p.thickness}
          />
        </>
      ) : (
        <path d={draftingPath(p)} />
      )}
      {p.kind === "leader" && p.points.length > 1 && (
        <>
          <path
            d="M8,-4 L0,0 L8,4"
            transform={`translate(${a.x},${a.y}) rotate(${(Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI})`}
          />
          <text
            x={b.x + 6}
            y={b.y - 6}
            fill={p.color}
            stroke="none"
            fontFamily="Inter, Arial, sans-serif"
            fontSize={14}
          >
            {p.label}
          </text>
        </>
      )}
    </g>
  );
}
export class DraftingUtil extends ShapeUtil<DraftingShape> {
  static override type = "drafting" as const;
  static override props = draftingProps;
  override getDefaultProps() {
    return structuredClone(draftingDefaults);
  }
  override canResize() {
    return false;
  }
  override canEdit() {
    return false;
  }
  override getGeometry(shape: DraftingShape) {
    const p = shape.props,
      a = p.points[0],
      b = p.points.at(-1)!;
    if (p.kind === "wall") return wallGeometry(p);
    if (p.kind === "circle") {
      const r = Math.max(0.1, Math.hypot(b.x - a.x, b.y - a.y));
      return new Circle2d({
        x: a.x - r,
        y: a.y - r,
        radius: r,
        isFilled: false,
      });
    }
    if (p.kind === "door" || p.kind === "window")
      return new Rectangle2d({
        x: -p.width / 2,
        y: p.kind === "door" && p.flip ? -p.width : -p.thickness / 2,
        width: Math.max(1, p.width),
        height: p.kind === "door" ? p.width + p.thickness / 2 : p.thickness,
        isFilled: false,
      });
    const points = (p.kind === "curve" ? curvePoints(p.points) : p.points).map(
      (v) => new Vec(v.x, v.y),
    );
    const line = new Polyline2d({ points });
    if (p.kind === "leader")
      return new Group2d({
        children: [
          line,
          new Rectangle2d({
            x: b.x + 6,
            y: b.y - 20,
            width: Math.max(24, p.label.length * 8),
            height: 20,
            isFilled: true,
            isLabel: true,
          }),
        ],
      });
    return line;
  }
  override component(shape: DraftingShape) {
    return (
      <SVGContainer style={{ overflow: "visible" }}>
        <DraftingGlyph shape={shape} />
      </SVGContainer>
    );
  }
  override toSvg(shape: DraftingShape) {
    return <DraftingGlyph shape={shape} />;
  }
  override getIndicatorPath(shape: DraftingShape) {
    const p = shape.props,
      path = new Path2D();
    if (p.kind === "circle") {
      const [a, b] = p.points;
      path.arc(a.x, a.y, Math.hypot(b.x - a.x, b.y - a.y), 0, 2 * Math.PI);
      return path;
    }
    if (p.kind === "door" || p.kind === "window") {
      const bounds = this.getGeometry(shape).bounds;
      path.rect(bounds.x, bounds.y, bounds.w, bounds.h);
      return path;
    }
    return new Path2D(draftingPath(p));
  }
  override getHandles(shape: DraftingShape) {
    return ["door", "window"].includes(shape.props.kind)
      ? []
      : shape.props.points.map((p, i) => ({
          id: String(i),
          type: "vertex" as const,
          index: `a${i}` as any,
          x: p.x,
          y: p.y,
          canBind: false,
        }));
  }
  override onHandleDrag(
    shape: DraftingShape,
    info: HandleDragInfo<DraftingShape>,
  ) {
    return {
      ...shape,
      props: {
        ...shape.props,
        points: shape.props.points.map((p, i) =>
          i === Number(info.handle.id)
            ? { x: info.handle.x, y: info.handle.y }
            : p,
        ),
      },
    };
  }
}
